import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Package,
  Truck,
  CheckCircle2,
  Clock,
  XCircle,
  ExternalLink,
  MapPin,
  HelpCircle,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Carrier tracking URL builders — when set, the public page links out to the
// carrier's official tracking page for real-time updates.
const CARRIER_URLS = {
  USPS: (tn) => `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${encodeURIComponent(tn)}`,
  UPS: (tn) => `https://www.ups.com/track?tracknum=${encodeURIComponent(tn)}`,
  FedEx: (tn) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tn)}`,
  DHL: (tn) => `https://www.dhl.com/en/express/tracking.html?AWB=${encodeURIComponent(tn)}`,
};

// Workflow stages rendered as a left-to-right progress bar.
const STAGES = [
  { key: 'processing', label: 'Processing', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2 },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: Package },
];

const OrderTracking = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [brand, setBrand] = useState({ brand_color: '#0ea5e9', logo_url: '', business_name: 'ShopNFCTags' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Load brand for consistent header
        try {
          const s = await axios.get(`${API_URL}/api/settings/public`);
          if (!cancelled) setBrand(s.data.brand || brand);
        } catch (_) { /* non-fatal */ }

        const res = await axios.get(`${API_URL}/api/orders/track/${token}`);
        if (!cancelled) setOrder(res.data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 404) {
            setError('Tracking link not found or tracking is not enabled for this order.');
          } else {
            setError('Something went wrong loading your tracking info. Please try again.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const statusIndex = order ? STAGES.findIndex((s) => s.key === order.order_status) : -1;
  const isCancelled = order?.order_status === 'cancelled';
  const brandColor = brand.brand_color || '#0ea5e9';

  const carrierUrl = order?.carrier && order?.tracking_number && CARRIER_URLS[order.carrier]
    ? CARRIER_URLS[order.carrier](order.tracking_number)
    : null;

  return (
    <div className="min-h-screen bg-[#0b1220] text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#0b1220]">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="track-header-home">
            {brand.logo_url ? (
              <>
                <img src={brand.logo_url} alt={brand.business_name} className="h-9 max-w-[180px] object-contain" />
                <span className="hidden sm:inline text-base font-semibold tracking-tight text-gray-200">
                  {brand.business_name}
                </span>
              </>
            ) : (
              <span className="text-xl font-bold tracking-tight">{brand.business_name}</span>
            )}
          </Link>
          <span className="text-xs uppercase tracking-widest text-gray-500">Order Tracking</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="order-tracking-page">
        {loading && (
          <div className="flex items-center gap-3 text-gray-400 justify-center py-20">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading your order…
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/15 border border-red-500/40 mb-5">
              <HelpCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">We can't find that order</h1>
            <p className="text-gray-400 mb-6" data-testid="tracking-error-msg">{error}</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-medium transition">
              Back to shop
            </Link>
          </div>
        )}

        {!loading && order && (
          <>
            <div className="mb-10">
              <p className="text-sm text-gray-500 uppercase tracking-widest mb-2">Order</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="tracking-order-id">
                <code className="text-white">{order.id}</code>
              </h1>
              <p className="text-gray-400 mt-1">Hi {order.customer_name}, here's the latest on your order.</p>
            </div>

            {/* Progress bar */}
            {!isCancelled ? (
              <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6 md:p-8 mb-8">
                <div className="flex justify-between items-center relative">
                  {/* The line behind the dots */}
                  <div className="absolute top-5 left-[10%] right-[10%] h-[3px] bg-gray-700 rounded" />
                  <div
                    className="absolute top-5 left-[10%] h-[3px] rounded transition-all duration-700"
                    style={{
                      width: `${statusIndex > 0 ? (statusIndex / (STAGES.length - 1)) * 80 : 0}%`,
                      background: brandColor,
                    }}
                  />
                  {STAGES.map((stage, idx) => {
                    const Icon = stage.icon;
                    const active = idx <= statusIndex;
                    const current = idx === statusIndex;
                    return (
                      <div
                        key={stage.key}
                        className="relative z-10 flex flex-col items-center gap-2 flex-1"
                        data-testid={`tracking-stage-${stage.key}`}
                      >
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                            active
                              ? 'text-white'
                              : 'bg-gray-900 border-gray-700 text-gray-500'
                          } ${current ? 'ring-4 ring-sky-500/20' : ''}`}
                          style={active ? { background: brandColor, borderColor: brandColor } : {}}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className={`text-xs md:text-sm font-medium ${active ? 'text-white' : 'text-gray-500'}`}>
                          {stage.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-red-500/10 border border-red-500/40 rounded-2xl p-6 mb-8 flex items-center gap-3" data-testid="tracking-cancelled-banner">
                <XCircle className="w-8 h-8 text-red-400" />
                <div>
                  <h3 className="font-semibold">Order cancelled</h3>
                  <p className="text-sm text-gray-400">If this wasn't you, please reach out to our support team.</p>
                </div>
              </div>
            )}

            {/* Tracking number block */}
            {order.tracking_number && (
              <div className="bg-sky-500/10 border border-sky-500/30 rounded-2xl p-6 mb-8" data-testid="tracking-number-block">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-sky-300 mb-1">Tracking Number</p>
                    <p className="font-mono text-lg text-white">{order.tracking_number}</p>
                    {order.carrier && <p className="text-sm text-gray-400 mt-1">Carrier: {order.carrier}</p>}
                  </div>
                  {carrierUrl && (
                    <a
                      href={carrierUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-medium text-white transition hover:opacity-90"
                      style={{ background: brandColor }}
                      data-testid="tracking-carrier-link"
                    >
                      Track on {order.carrier} <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Items */}
            <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6 mb-8">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-sky-400" /> Items in this order
              </h2>
              <ul className="divide-y divide-gray-800">
                {(order.items || []).map((item, idx) => (
                  <li key={idx} className="py-3 flex items-center justify-between">
                    <span className="text-white">{item.title}</span>
                    <span className="text-gray-400 text-sm">Qty: {item.quantity}</span>
                  </li>
                ))}
                {(!order.items || order.items.length === 0) && (
                  <li className="py-3 text-gray-500 text-sm">No items listed.</li>
                )}
              </ul>
            </div>

            {/* Shipping method */}
            {order.shipping_method && (
              <div className="flex items-center gap-3 text-gray-400 text-sm mb-10">
                <MapPin className="w-4 h-4 text-sky-400" />
                <span>Shipping: {order.shipping_method}</span>
              </div>
            )}

            <div className="text-center py-8 border-t border-gray-800">
              <p className="text-sm text-gray-500 mb-4">Questions about your order?</p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-medium transition" data-testid="tracking-contact-link">
                Contact support
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default OrderTracking;
